/* Staff Verifier — SV-01…SV-17, full outcome matrix (Bible §42–45, §13).
   Calm, exact, operational: zero Spark, zero play. Every outcome shows the
   staff action and a customer-safe sentence to say out loud (TOV §22).
   Redemption is atomic and always wins (locked #16): self-undo 2 min (same
   actor, same device), manager undo 15 min with reason, beyond that support.
   Test harness: ?ecran= forces a screen, demo codes drive the outcomes. */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import './till.css'

type Ecran =
  | 'enrol' | 'enrol-fail' | 'enrol-ok'
  | 'identity' | 'lockout' | 'idle'
  | 'home' | 'manual'
  | 'valid' | 'redeemed' | 'already' | 'wrong' | 'timing' | 'risk' | 'offline'
  | 'undo-manager' | 'undo-refused'
  | 'devices'

const ACTORS = ['Nadia', 'Cyril', 'Marc', 'Léna']
const PIN_OK = '1234' // test harness only — real PINs live server-side
const SELF_UNDO_S = 120 // L8 · locked #16
const MANAGER_UNDO_S = 15 * 60
const LOCKOUT_S = 5 * 60 // Q3 locked
const IDLE_S = 5 * 60 // Q3 locked

const GIFT = { label: 'Soin profond offert', holder: 'Léa M.', until: '27 octobre', uses: 'une utilisation' }

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`

/** Demo router: the manual code (or simulated scan) picks the outcome. */
function outcomeFor(code: string): Ecran {
  const c = code.toUpperCase().replace(/[\s-]/g, '')
  if (c.includes('DEJA')) return 'already'
  if (c.includes('AILLEURS')) return 'wrong'
  if (c.includes('BIENTOT') || c.includes('EXPIRE')) return 'timing'
  if (c.includes('RISQUE')) return 'risk'
  if (c.includes('HORSLIGNE')) return 'offline'
  return 'valid'
}

export default function Till() {
  const [params] = useSearchParams()
  const forced = params.get('ecran') as Ecran | null
  const [ecran, setEcran] = useState<Ecran>(forced ?? 'enrol')
  const [actor, setActor] = useState<string>(forced && !['enrol', 'enrol-fail', 'enrol-ok', 'identity', 'lockout'].includes(forced) ? 'Nadia' : '')
  const [picked, setPicked] = useState('Nadia')
  const [pin, setPin] = useState('')
  const [pinTries, setPinTries] = useState(0)
  const [code, setCode] = useState('')
  const [timingExpired, setTimingExpired] = useState(params.get('variante') === 'expire')
  const [reason, setReason] = useState('')
  const [homeNote, setHomeNote] = useState('')
  const [speed, setSpeed] = useState(1) // test harness — removed with the mocks
  const [showCodes, setShowCodes] = useState(false)
  const [now, setNow] = useState(Date.now())
  const redeemedAt = useRef<number | null>(forced === 'redeemed' || forced === 'undo-refused' || forced === 'undo-manager' ? Date.now() - (forced === 'redeemed' ? 0 : SELF_UNDO_S * 1000 + 60_000) : null)
  const lockedAt = useRef<number | null>(forced === 'lockout' ? Date.now() : null)
  const lastActivity = useRef(Date.now())

  /* one shared 250 ms tick drives every window: undo, lockout, idle */
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(t)
  }, [])

  const elapsed = (since: number | null) => since === null ? 0 : Math.floor((now - since) * speed / 1000)
  const selfLeft = SELF_UNDO_S - elapsed(redeemedAt.current)
  const managerLeftMin = Math.max(0, Math.ceil((MANAGER_UNDO_S - elapsed(redeemedAt.current)) / 60))
  const lockLeft = LOCKOUT_S - elapsed(lockedAt.current)

  /* SV-16 · idle re-auth: 5 min without activity on a working screen */
  const working = actor !== '' && !['identity', 'lockout', 'idle', 'enrol', 'enrol-fail', 'enrol-ok'].includes(ecran)
  useEffect(() => {
    if (!working) return
    if ((now - lastActivity.current) * speed / 1000 > IDLE_S) setEcran('idle')
  }, [now, working, speed])
  const touch = () => { lastActivity.current = Date.now() }

  /* unlock the lockout when its window closes */
  useEffect(() => {
    if (ecran === 'lockout' && lockLeft <= 0 && !forced) { lockedAt.current = null; setPinTries(0); setEcran('identity') }
  }, [now])

  const tapPin = (d: string) => {
    const next = pin + d
    setPin(next)
    if (next.length < 4) return
    if (next === PIN_OK) {
      setActor(picked); setPin(''); setPinTries(0); touch(); setEcran('home')
    } else {
      const tries = pinTries + 1
      setPin(''); setPinTries(tries)
      if (tries >= 5) { lockedAt.current = Date.now(); setEcran('lockout') } // SV-14
    }
  }

  const validate = (c: string) => { touch(); setHomeNote(''); setEcran(outcomeFor(c)) }
  const confirmUse = () => { redeemedAt.current = Date.now(); touch(); setEcran('redeemed') } // atomic — SV-06
  const tryUndo = () => {
    touch()
    if (selfLeft > 0) { redeemedAt.current = null; setHomeNote('Validation annulée — le cadeau redevient actif.'); setEcran('home') }
    else setEcran('undo-refused') // SV-17 — redemption always wins, the tap is never swallowed
  }
  const managerUndo = () => {
    redeemedAt.current = null; setReason(''); touch()
    setHomeNote('Validation annulée par un responsable — le cadeau redevient actif.')
    setEcran('home')
  }

  const band = ecran.startsWith('enrol')
    ? <>ESPACE ÉQUIPE <small>Enrôlement</small></>
    : ecran === 'devices'
      ? <>VALESSIO <small>Appareils & sessions</small></>
      : <>VALESSIO — PARIS 9 <small>{actor || 'Appareil caisse 1'}</small></>

  return (
    <div className="sv" onPointerDown={touch}>
      <div className="sv-frame">
        <div className="sv-band">{band}</div>

        {/* ---------- SV-01 · device enrolment ---------- */}
        {ecran === 'enrol' && (
          <div className="sv-body">
            <h1 className="sv-h">Enrôler cet appareil</h1>
            <p className="sv-sub">Scannez le QR d’enrôlement affiché par votre responsable.</p>
            <div className="sv-qr" aria-hidden />
            <p className="sv-micro" style={{ textAlign: 'center' }}>Ce lien expire dans 10 minutes et ne sert qu’une fois.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('enrol-ok')}>Simuler le scan du QR</button>
            <button className="sv-btn sv-btn-quiet" onClick={() => setEcran('enrol-fail')}>Simuler un QR expiré</button>
          </div>
        )}

        {/* ---------- SV-15 · enrolment failed ---------- */}
        {ecran === 'enrol-fail' && (
          <div className="sv-body">
            <div className="sv-outcome crit"><span className="mark">✕</span> QR expiré</div>
            <p className="sv-sub">Ce QR d’enrôlement a expiré (10 min). Demandez-en un nouveau à votre responsable.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('enrol')}>Réessayer avec un nouveau QR</button>
          </div>
        )}

        {/* ---------- SV-15 · enrolment success ---------- */}
        {ecran === 'enrol-ok' && (
          <div className="sv-body">
            <div className="sv-outcome ok"><span className="mark">✓</span> Appareil enrôlé</div>
            <p className="sv-sub"><b>Caisse 1 · Valessio Paris 9</b>. L’équipe peut maintenant s’identifier par PIN.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('identity')}>Commencer à valider</button>
          </div>
        )}

        {/* ---------- SV-02 · staff identity (personal PIN on the shared device) ---------- */}
        {ecran === 'identity' && (
          <div className="sv-body">
            <h1 className="sv-h">Qui êtes-vous&nbsp;?</h1>
            <div className="sv-actors">
              {ACTORS.map(a => (
                <button key={a} className={'sv-actor' + (picked === a ? ' on' : '')}
                  onClick={() => { setPicked(a); setPin(''); }}>{a}</button>
              ))}
            </div>
            <div className="sv-pins" aria-label="PIN">
              {[0, 1, 2, 3].map(i => <i key={i} className={i < pin.length ? 'full' : ''} />)}
            </div>
            {pinTries > 0 && <p className="sv-pinerr">PIN incorrect — essai {pinTries}/5.</p>}
            <div className="sv-pad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k, i) =>
                k === '' ? <span key={i} /> :
                  <button key={i} onClick={() => k === '⌫' ? setPin(pin.slice(0, -1)) : tapPin(k)}>{k}</button>)}
            </div>
            <p className="sv-note">PIN personnel — ou empreinte/passkey si configuré</p>
            <div className="sv-foot"><button onClick={() => setEcran('devices')}>Responsable&nbsp;: appareils & sessions</button></div>
          </div>
        )}

        {/* ---------- SV-14 · PIN lockout — the till keeps working for others ---------- */}
        {ecran === 'lockout' && (
          <div className="sv-body">
            <div className="sv-outcome crit"><span className="mark">✕</span> Trop d’essais</div>
            <p className="sv-sub">Le PIN de <b>{picked}</b> est verrouillé pour <span className="sv-clock crit">{fmt(lockLeft)}</span>. Un autre membre peut se connecter normalement.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-quiet" onClick={() => { lockedAt.current = null; setPinTries(0); setEcran('identity') }}>Déverrouiller (responsable)</button>
          </div>
        )}

        {/* ---------- SV-16 · idle re-auth ---------- */}
        {ecran === 'idle' && (
          <div className="sv-body">
            <h1 className="sv-h">De retour&nbsp;?</h1>
            <p className="sv-sub">5 minutes sans activité — identifiez-vous pour continuer.</p>
            <div className="sv-actors">
              {ACTORS.map(a => (
                <button key={a} className="sv-actor"
                  onClick={() => { setPicked(a); setActor(''); setPin(''); touch(); setEcran('identity') }}>{a}</button>
              ))}
            </div>
            <div className="sv-spacer" />
            <p className="sv-note">Staff multi-établissements&nbsp;: le bandeau affiche l’établissement actif.</p>
          </div>
        )}

        {/* ---------- SV-03 · home — one job on one screen ---------- */}
        {ecran === 'home' && (
          <div className="sv-body">
            <h1 className="sv-h">Valider un cadeau</h1>
            <p className="sv-sub">Scannez le QR du client ou saisissez son code.</p>
            {homeNote && <div className="sv-say">✓ {homeNote}</div>}
            <div className="sv-cam">ZONE CAMÉRA</div>
            <button className="sv-btn sv-btn-primary" onClick={() => validate('PRIZE-4K7T-9QX2')}>Scanner le QR</button>
            <button className="sv-btn sv-btn-quiet" onClick={() => { touch(); setEcran('manual') }}>Saisir le code</button>
            <div className="sv-spacer" />
          </div>
        )}

        {/* ---------- SV-04 · manual code — ambiguity-safe alphabet ---------- */}
        {ecran === 'manual' && (
          <div className="sv-body">
            <h1 className="sv-h">Saisir le code</h1>
            <input className="sv-code-in" autoFocus placeholder="XXXX-XXXX-XXXX"
              value={code} onChange={e => setCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && code.trim()) validate(code) }} />
            <p className="sv-micro">Lettres et chiffres — O/0 et 1/I ne sont jamais ambigus.</p>
            <button className="sv-btn sv-btn-primary" disabled={!code.trim()} onClick={() => validate(code)}>Vérifier</button>
            <button className="sv-btn sv-btn-quiet" onClick={() => setEcran('home')}>Retour</button>
            <div className="sv-spacer" />
          </div>
        )}

        {/* ---------- SV-05 · valid → confirm (staff sees the minimum) ---------- */}
        {ecran === 'valid' && (
          <div className="sv-body">
            <div className="sv-outcome ok"><span className="mark">✓</span> Cadeau valide</div>
            <div className="sv-gift">
              <span className="name">{GIFT.label}</span>
              <span className="meta">{GIFT.holder} · valable ici jusqu’au {GIFT.until} · {GIFT.uses}</span>
              <span className="cond">Conditions v.&nbsp;12 · <u>voir</u></span>
            </div>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={confirmUse}>Confirmer l’utilisation</button>
            <button className="sv-btn sv-btn-quiet" onClick={() => setEcran('home')}>Annuler</button>
          </div>
        )}

        {/* ---------- SV-06 · redeemed — atomic + self-undo window (L8) ---------- */}
        {ecran === 'redeemed' && (
          <div className="sv-body">
            <div className="sv-outcome ok"><span className="mark">✓</span> Cadeau utilisé</div>
            <p className="sv-receipt">Validation enregistrée à {new Date(redeemedAt.current ?? now).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} par {actor || 'Nadia'} — Paris 9.</p>
            {selfLeft > 0
              ? <p className="sv-sub">Erreur&nbsp;? Vous pouvez annuler vous-même pendant <span className="sv-clock">{fmt(selfLeft)}</span>.</p>
              : <p className="sv-sub">La fenêtre d’annulation de 2 minutes est passée — un responsable peut encore corriger.</p>}
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => { touch(); setHomeNote(''); setEcran('home') }}>Valider un autre cadeau</button>
            <button className="sv-btn sv-btn-quiet" onClick={tryUndo}>Annuler la validation</button>
          </div>
        )}

        {/* ---------- SV-17 · undo refused — redemption always wins, no dead-end ---------- */}
        {ecran === 'undo-refused' && (
          <div className="sv-body">
            <div className="sv-outcome crit"><span className="mark">✕</span> Annulation impossible</div>
            <div className="sv-gift">
              <span className="name">{GIFT.label}</span>
              <span className="meta">validé il y a {Math.max(3, Math.floor(elapsed(redeemedAt.current) / 60))}&nbsp;min · ✕ Déjà utilisé</span>
            </div>
            <p className="sv-sub">Ce cadeau a été remis au client et la fenêtre d’annulation de 2 minutes est passée. Un responsable peut encore corriger pendant {managerLeftMin}&nbsp;minutes.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => { touch(); setEcran('undo-manager') }}>Demander à un responsable</button>
            <p className="sv-note">Rien n’a été perdu&nbsp;: la validation reste tracée et corrigible.</p>
          </div>
        )}

        {/* ---------- SV-12 · manager undo — 15 min, reason required ---------- */}
        {ecran === 'undo-manager' && (
          <div className="sv-body">
            <h1 className="sv-h">Annuler la validation&nbsp;?</h1>
            <div className="sv-gift">
              <span className="name">{GIFT.label}</span>
              <span className="meta">validé il y a {Math.max(4, Math.floor(elapsed(redeemedAt.current) / 60))}&nbsp;min par {actor || 'Nadia'}</span>
            </div>
            <p className="sv-micro">Motif</p>
            <div className="sv-reasons">
              {['Erreur de saisie', 'Client reparti sans utiliser'].map(r => (
                <button key={r} className={'sv-reason' + (reason === r ? ' on' : '')} onClick={() => setReason(r)}>{r}</button>
              ))}
            </div>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-danger" disabled={!reason} onClick={managerUndo}>Annuler la validation</button>
            <p className="sv-note">Le cadeau redevient actif. Passé 15 minutes, la correction passe par le support.</p>
          </div>
        )}

        {/* ---------- SV-07 · already redeemed — safe escalation, no accusation ---------- */}
        {ecran === 'already' && (
          <div className="sv-body">
            <div className="sv-outcome crit"><span className="mark">✕</span> Déjà utilisé</div>
            <p className="sv-sub">Le 12 août à 14:02, chez Paris 9. Rien à corriger — le cadeau a été honoré.</p>
            <div className="sv-say">À dire au client&nbsp;: <b>«&nbsp;Ce cadeau a déjà été utilisé.&nbsp;»</b></div>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-quiet" onClick={() => setEcran('home')}>Demander à un responsable</button>
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('home')}>Valider un autre cadeau</button>
          </div>
        )}

        {/* ---------- SV-08 · wrong location — override only if the campaign permits ---------- */}
        {ecran === 'wrong' && (
          <div className="sv-body">
            <div className="sv-outcome warn"><span className="mark">⚠</span> Pas valable ici</div>
            <p className="sv-sub">Utilisable à&nbsp;: <b>Valessio — Marais</b>, <b>Valessio — Batignolles</b>.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-quiet" onClick={() => setEcran('home')}>Autoriser ici (responsable)</button>
            <p className="sv-note">Possible uniquement si la campagne le permet — action tracée.</p>
          </div>
        )}

        {/* ---------- SV-09 · not yet valid / expired — exact dates, next action ---------- */}
        {ecran === 'timing' && (
          <div className="sv-body">
            <div className="sv-outcome warn"><span className="mark">◷</span> {timingExpired ? 'Expiré' : 'Pas encore valable'}</div>
            {timingExpired
              ? <div className="sv-say">À dire au client&nbsp;: <b>«&nbsp;Ce cadeau a expiré le 12 août.&nbsp;»</b> Demandez une exception à un responsable si nécessaire.</div>
              : <div className="sv-say">À dire au client&nbsp;: <b>«&nbsp;Ce cadeau sera disponible le 30 août.&nbsp;»</b></div>}
            <div className="sv-spacer" />
            {timingExpired && <button className="sv-btn sv-btn-quiet" onClick={() => setEcran('home')}>Exception responsable</button>}
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('home')}>Valider un autre cadeau</button>
            <button className="sv-btn sv-btn-quiet" onClick={() => setTimingExpired(!timingExpired)}>
              Voir le cas «&nbsp;{timingExpired ? 'pas encore valable' : 'expiré'}&nbsp;»
            </button>
          </div>
        )}

        {/* ---------- SV-10 · high risk — manager review, no accusation ---------- */}
        {ecran === 'risk' && (
          <div className="sv-body">
            <div className="sv-outcome warn"><span className="mark">⚠</span> Vérification nécessaire</div>
            <div className="sv-say">À dire au client&nbsp;: <b>«&nbsp;Un responsable doit vérifier ce cadeau.&nbsp;»</b></div>
            <p className="sv-sub">Aucune accusation — le système a repéré une anomalie à contrôler.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('home')}>Appeler un responsable</button>
          </div>
        )}

        {/* ---------- SV-11 · offline — never a false success ---------- */}
        {ecran === 'offline' && (
          <div className="sv-body">
            <div className="sv-outcome warn"><span className="mark">⚠</span> Connexion nécessaire</div>
            <p className="sv-sub">Reconnectez-vous avant de remettre le cadeau. Aucune validation n’est confirmée hors ligne.</p>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('home')}>Réessayer</button>
          </div>
        )}

        {/* ---------- SV-13 · devices & sessions (manager) ---------- */}
        {ecran === 'devices' && (
          <div className="sv-body">
            <h1 className="sv-h">Appareils & sessions</h1>
            <div className="sv-rows">
              <div className="sv-row">
                <div className="grow"><div className="t">Caisse 1 — iPhone</div><div className="m">Paris 9 · vu il y a 2 min</div></div>
                <span className="sv-live">Actif</span>
              </div>
              <div className="sv-row">
                <div className="grow"><div className="t">Tablette accueil</div><div className="m">Paris 9 · vu hier</div></div>
                <button className="act">révoquer</button>
              </div>
              <div className="sv-row">
                <div className="grow"><div className="t">Nadia — session</div><div className="m">ouverte 13:40</div></div>
                <button className="act">fermer</button>
              </div>
            </div>
            <div className="sv-spacer" />
            <button className="sv-btn sv-btn-primary" onClick={() => setEcran('enrol')}>Nouvel enrôlement</button>
            <button className="sv-btn sv-btn-quiet" onClick={() => setEcran(actor ? 'home' : 'identity')}>Retour</button>
          </div>
        )}
      </div>

      {/* test harness — demo codes + speed, removed when the seam connects the Worker */}
      {showCodes && (
        <div className="sv-devcodes">
          <b>Codes de test</b>
          <span>PRIZE-4K7T-9QX2 → valide</span>
          <span>DEJA → déjà utilisé</span>
          <span>AILLEURS → autre établissement</span>
          <span>EXPIRE / BIENTOT → dates</span>
          <span>RISQUE → vérification</span>
          <span>HORS-LIGNE → hors ligne</span>
          <span>PIN de test : 1234</span>
        </div>
      )}
      <div className="sv-dev" aria-label="Outils de test">
        <button className={speed === 1 ? 'on' : ''} onClick={() => setSpeed(1)}>1×</button>
        <button className={speed === 10 ? 'on' : ''} onClick={() => setSpeed(10)}>10×</button>
        <button className={showCodes ? 'on' : ''} onClick={() => setShowCodes(!showCodes)}>codes</button>
      </div>
    </div>
  )
}
